<template>
    <tailwind-layout title="Bank Accounts">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">Bank Accounts</h1>
                <div class="flex items-center space-x-4">
                    <button @click="createLinkToken('auth')"
                        class="primaryButton">
                        Add Bank Account
                    </button>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <h1 class="font-semibold text-2xl leading-tight">Bank Accounts</h1>
                    <button @click="createLinkToken('auth')"
                        class="primaryButton">
                        Add Bank Account
                    </button>
                </div>
            </div>
        </template>
        <div class="overflow-hidden">
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Account Last 4 Digits
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Bank Name
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Account Title
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(account, index) in accounts" :key="account.id"
                                            :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer')]"
                                            >
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ account.account_last_4 }}
                                                <span
                                                    v-if="account.is_default"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50  text-green-700 ring-green-600/20 ml-2"
                                                    >
                                                    Default
                                                </span>
                                                <span
                                                    v-if="account.status === 'needs_update'"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-yellow-50  text-yellow-700 ring-yellow-600/20 ml-2"
                                                    >
                                                    Needs Update
                                                </span>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ account.bank_name }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ account.account_name }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div class="flex items-center justify-end space-x-2">
                                                    <InertiaLink v-if="!account.is_default" :href="route('user-bank-accounts.make-default', account.id)"
                                                        class="text-blue-600 hover:text-indigo-900">
                                                        Make Default
                                                    </InertiaLink>
                                                    <button
                                                        v-if="account.status === 'needs_update'"
                                                        @click="updateAccount(account.id)"
                                                        type="button"
                                                        class="text-blue-600 hover:text-indigo-900">
                                                        Update Account
                                                    </button>
                                                    <button
                                                        @click="confirmDelete(account)"
                                                        type="button"
                                                        class="text-red-600 hover:text-red-900">
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-show="!accounts.length">
                                            <td colspan="4"
                                                class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No bank accounts were found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                 <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                 <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(account, index) in accounts" :key="account.id" class="bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200 col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">
                                                **** **** **** {{ account.account_last_4 }}
                                                <span
                                                    v-if="account.is_default"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50  text-green-700 ring-green-600/20 ml-2"
                                                    >
                                                    Default
                                                </span>
                                                <span
                                                    v-if="account.status === 'needs_update'"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-yellow-50  text-yellow-700 ring-yellow-600/20 ml-2"
                                                    >
                                                    Needs Update
                                                </span>
                                            </h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Bank Name:</span> {{ account.bank_name }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Account Title:</span> {{ account.account_name }}</h4>
                                        </div>
                                        <div>
                                            <div class="divide-gray-200 -mt-px flex divide-x">
                                                <div v-if="!account.is_default" class="flex w-0 flex-1">
                                                    <inertia-link
                                                        title="Make account default"
                                                        :href="route('user-bank-accounts.make-default', account.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-star" /> Make Default
                                                    </inertia-link>
                                                </div>
                                                <div v-if="account.status === 'needs_update'" class="flex w-0 flex-1">
                                                    <button
                                                        @click="updateAccount(account.id)"
                                                        title="Update account"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <i class="fa fa-sync"></i> Update
                                                    </button>
                                                </div>
                                                <div v-else class="flex w-0 flex-1">
                                                    <button
                                                        @click="confirmDelete(account)"
                                                        title="Delete account"
                                                        class="hover:bg-red-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-red-600">
                                                        <i class="fa fa-trash"></i> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Delete Confirmation Modal -->
        <jetstreem-modal :show="showDeleteModal" @close="showDeleteModal = false" :max-width="'2xl'">
            <div class="m-5">
                <h2 class="text-xl font-semibold text-gray-900 text-left mb-4">
                    Delete Bank Account
                </h2>
                
                <p class="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete this bank account?
                </p>
                
                <div v-if="accountToDelete" class="mt-4 p-4 bg-gray-50 rounded-md">
                    <p class="text-sm font-medium text-gray-900">
                        <strong>Account:</strong> {{ accountToDelete.bank_name }} (**** **** **** {{ accountToDelete.account_last_4 }})
                    </p>
                    <p v-if="accountToDelete.is_default" class="mt-2 text-sm text-yellow-600">
                        This is your default account. Another account will be set as default if available.
                    </p>
                </div>
                
                <p class="mt-4 text-sm text-gray-600 mb-4">
                    This action cannot be undone. The account will be removed from Plaid and marked as inactive.
                </p>
                
                <div class="pt-5">
                    <div class="flex justify-end space-x-3">
                        <button
                            @click="showDeleteModal = false"
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            @click="deleteAccount"
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                            Delete Account
                        </button>
                    </div>
                </div>
            </div>
        </jetstreem-modal>
        
        <full-page-loader v-if="isLoader"></full-page-loader>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import axios from "axios";
import { router } from '@inertiajs/vue3';
import FullPageLoader from "@/Shared/FullPageLoader.vue";
import Modal from "@/Shared/Modal.vue";
import JetstreemModal from "@/Jetstream/Modal.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faCheckCircle, faCheck } from "@fortawesome/pro-solid-svg-icons";
import FormButton from '@/Shared/FormButton.vue'

export default {
    components: {
        FullPageLoader,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        Modal,
        JetstreemModal,
        FontAwesomeIcon,
        FormButton
    },
    props: {
        accounts: Object,
    },
    data() {
        return {
            faCheckCircle,
            faCheck,
            isLoader: false,
            showDeleteModal: false,
            accountToDelete: null,
        }
    },

    mounted() {
        
    },

    methods: {
        async createLinkToken(product) {
            this.isLoader = true;
            try {
                const response = await axios.post(route('plaid.create-link-token'), {
                    product: product,
                });
                if (response.data.status) {
                    this.initializePlaidLink(response.data.public_token, product);
                }

                // Handle the successful response here
            } catch (error) {
                // Handle any errors here
            }
        },

        async initializePlaidLink(public_token, product) {
            this.isShowStepsModal = false;
            this.isLoader = false;
            var handler = Plaid.create({
                token: public_token,  // Use the provided public token directly
                onLoad: function () {
                    // Optional, called when Link loads
                },
                onSuccess: (public_token, metadata) => {
                    this.isLoader = true;
                    if (product === 'auth') {
                        this.finishAuth(public_token, metadata);
                    } else if (product === 'identity_verification') {
                        this.finishIdv(public_token, metadata);
                    }
                },
                onExit: function (err, metadata) {
                    // The user exited the Link flow.
                    if (err != null) {
                        // The user encountered a Plaid API error prior to exiting.
                    }
                    // metadata contains information about the institution
                    // that the user selected and the most recent API request IDs.
                    // Storing this information can be helpful for support.
                },
                onEvent: function (eventName, metadata) {
                    // Optionally capture Link flow events, streamed through
                    // this callback as your users connect an Item to Plaid.
                    // For example:
                    // eventName = "TRANSITION_VIEW"
                    // metadata  = {
                    //   link_session_id: "123-abc",
                    //   mfa_type:        "questions",
                    //   timestamp:       "2017-09-14T14:42:19.350Z",
                    //   view_name:       "MFA",
                    // }
                }
            });

            handler.open();
        },

        async finishAuth(public_token, metadata) {
            const response = await axios.post(route('plaid.exchange-token-and-retrieve-account'), {
                public_token: public_token,
                metadata: metadata,
            });
            this.isLoader = false;
            router.reload();
        },
        async finishIdv(public_token, metadata) {
            const response = await axios.post(route('plaid.idv.submit'), {
                public_token: public_token,
                metadata: metadata,
            });
            this.isLoader = false;

            // Handle the successful response here
            router.reload();
        },

        canAccountBeUpdated(account) {
            // Check if account can be updated (has needs_update status)
            return account.status === 'needs_update';
        },

        confirmDelete(account) {
            this.accountToDelete = account;
            this.showDeleteModal = true;
        },

        async deleteAccount() {
            if (!this.accountToDelete) {
                return;
            }

            this.isLoader = true;
            this.showDeleteModal = false;

            try {
                router.delete(route('user-bank-accounts.delete', this.accountToDelete.id), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.accountToDelete = null;
                        this.isLoader = false;
                    },
                    onError: () => {
                        this.isLoader = false;
                    },
                });
            } catch (error) {
                this.isLoader = false;
            }
        },

        async updateAccount(accountId) {
            this.isLoader = true;
            try {
                const response = await axios.get(route('user-bank-accounts.update-link-token', accountId));
                
                if (response.data.status && response.data.link_token) {
                    this.initializePlaidLinkUpdate(response.data.link_token, accountId);
                } else {
                    alert('Failed to generate update link. Please try again.');
                    this.isLoader = false;
                }
            } catch (error) {
                alert('Failed to generate update link. Please try again.');
                this.isLoader = false;
            }
        },

        async initializePlaidLinkUpdate(link_token, accountId) {
            this.isLoader = false;
            var handler = Plaid.create({
                token: link_token,
                onLoad: function () {
                    // Optional, called when Link loads
                },
                onSuccess: (public_token, metadata) => {
                    this.isLoader = true;
                    // After successful update, exchange token and update account
                    this.finishUpdate(public_token, metadata, accountId);
                },
                onExit: (err, metadata) => {
                    // Reset loader when user exits
                    this.isLoader = false;
                    
                    // The user exited the Link flow.
                    if (err != null) {
                        // The user encountered a Plaid API error prior to exiting.
                        alert('An error occurred while updating your account: ' + (err.error_message || 'Please try again.'));
                    }
                },
                onEvent: function (eventName, metadata) {
                    // Optionally capture Link flow events
                }
            });

            handler.open();
        },

        async finishUpdate(public_token, metadata, accountId) {
            try {
                // Validate metadata structure before proceeding
                if (!metadata || !metadata.accounts || !Array.isArray(metadata.accounts) || metadata.accounts.length === 0) {
                    throw new Error('Invalid account metadata received from Plaid.');
                }

                // Exchange public token and update account information
                const response = await axios.post(route('user-bank-accounts.exchange-update-token', accountId), {
                    public_token: public_token,
                    metadata: metadata,
                });

                if (response.data.status) {
                    // Successfully updated - reload the page to show updated account info
                    router.reload({
                        preserveScroll: true,
                        onSuccess: () => {
                            this.isLoader = false;
                        },
                        onError: () => {
                            this.isLoader = false;
                        },
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to update account.');
                }
            } catch (error) {
                this.isLoader = false;
                
                // Show user-friendly error message
                const errorMessage = error.response?.data?.message 
                    || error.message 
                    || 'An error occurred while updating your account. Please try again.';
                alert(errorMessage);
            }
        },
    },
}
</script>
